import React, {
  ChangeEvent,
  MouseEvent,
  DragEvent,
  useRef,
  useState,
} from 'react';

import { FileDrop } from 'react-file-drop';
import {
  ControlLabel,
  FormControl,
  FormGroup,
  Glyphicon,
} from 'react-bootstrap';

import SimpleSelect from '../../utils/FormWidgets/SimpleSelect';
import { Attachment } from '../../../types/Activity';
import { mimeTypeToIconMap } from '../utils';
import * as ActivitiesService from '../../../services/ActivitiesService';

type Props = {
  activityId: string;
  onAttachmentUploaded: (data: Attachment) => Promise<void>;
  onClose: () => void;
};

export type UploadAttachmentData = Partial<
  Pick<Attachment, 'title' | 'type'>
> & {
  fileToUpload?: File;
};

const activityAttachmentTypes = [{ label: 'Menu', value: 'menu' }];
const acceptedFileTypes = {
  menu: {
    types: ['application/pdf'],
    errorMessage: "Only PDF files allowed for 'Menu' attachments",
  },
};
const getAcceptedFileTypesByAttachmentType = (attachmentType: string = '') => {
  if (!acceptedFileTypes[attachmentType]) return undefined;

  return acceptedFileTypes[attachmentType].types.join('|');
};

const UploadActivityAttachment = (props: Props) => {
  const [attachmentData, setAttachmentData] = useState<UploadAttachmentData>({
    title: '',
  });
  const [error, setError] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = (type: 'menu') => {
    setAttachmentData(prev => ({ ...prev, type }));
  };
  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setAttachmentData(prev => ({ ...prev, title: value }));
  };

  const handleChooseFileClick = () => {
    fileInputRef?.current?.click();
  };
  const handleFileDrop = (
    files: FileList | null,
    event: DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    handleFileSelected(files);
  };
  const handleFileInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    handleFileSelected(files);
  };
  const handleFileSelected = (files: FileList | null) => {
    if (!files || files.length === 0) return;

    if (attachmentData.type) {
      if (
        !acceptedFileTypes[attachmentData.type].types.includes(files[0].type)
      ) {
        setErrors({
          fileToUpload: acceptedFileTypes[attachmentData.type].errorMessage,
        });
        return;
      }
    }

    setAttachmentData(prev => ({ ...prev, fileToUpload: files[0] }));
  };
  const handleRemoveAttachment = () => {
    setAttachmentData(prev => ({ ...prev, fileToUpload: undefined }));
  };

  const isValid = (
    attachmentData: UploadAttachmentData
  ): attachmentData is Required<UploadAttachmentData> => {
    const errors: Record<string, string> = {};

    if (!attachmentData.title || attachmentData.title.trim().length === 0) {
      errors.title = 'required';
    }
    if (attachmentData.title && attachmentData.title.trim().length > 50) {
      errors.title =
        "'Title' length should be less than or equal to 50 characters";
    }
    if (!attachmentData.type) {
      errors.type = 'required';
    }
    if (!attachmentData.fileToUpload) {
      errors.fileToUpload = 'required';
    }

    if (attachmentData.type && attachmentData.fileToUpload) {
      if (
        !acceptedFileTypes[attachmentData.type].types.includes(
          attachmentData.fileToUpload.type
        )
      ) {
        errors.fileToUpload =
          acceptedFileTypes[attachmentData.type].errorMessage;
      }
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return false;
    }

    return true;
  };
  const handleUploadButtonClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setError('');
    setIsLoading(true);

    if (!isValid(attachmentData)) {
      setIsLoading(false);
      return;
    }

    try {
      const attachment = await ActivitiesService.uploadAttachment(
        props.activityId,
        attachmentData
      );

      await props.onAttachmentUploaded(attachment);
      props.onClose();
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setError(`Error: ${(e as Error).message}`);
    }
  };

  return (
    <div className={'activity__upload-attachment'}>
      <h4>Upload attachment</h4>
      <hr />
      <form className={'form clearfix'}>
        <FormGroup className={`${errors.title && 'has-error'}`}>
          <ControlLabel htmlFor='upload-attachment__title'>Title:</ControlLabel>
          <FormControl
            type='text'
            placeholder='Title'
            id={'upload-attachment__title'}
            onChange={handleTitleChange}
            value={attachmentData.title}
            aria-describedby={'upload-attachment__title--error'}
          />
          {errors.title && (
            <span
              id='upload-attachment__title--error'
              className='help-block text-right'
            >
              {errors.title}
            </span>
          )}
        </FormGroup>

        <FormGroup className={`${errors.type && 'has-error'}`}>
          <ControlLabel htmlFor='upload-attachment__type'>Type:</ControlLabel>
          <SimpleSelect
            // TODO: (fix build workaround) Need to be fixed next time when this code will be changed
            // @ts-ignore
            onChange={handleTypeChange}
            value={attachmentData.type}
            data={activityAttachmentTypes}
          />
          {errors.type && (
            <span
              id='upload-attachment__type--error'
              className='help-block text-right'
            >
              {errors.type}
            </span>
          )}
        </FormGroup>

        <FormGroup className={`${errors.fileToUpload && 'has-error'}`}>
          <ControlLabel htmlFor='upload-attachment__fileToUpload'>
            Attach document:
          </ControlLabel>
          {!attachmentData.fileToUpload && (
            <>
              <input
                type={'file'}
                className={'hidden'}
                ref={fileInputRef}
                onChange={handleFileInputChange}
                accept={getAcceptedFileTypesByAttachmentType(
                  attachmentData.type
                )}
              />
              <FileDrop onDrop={handleFileDrop}>
                <div className={'well well-lg text-center'}>
                  <span>
                    Drag & Drop or{' '}
                    <button
                      type={'button'}
                      className={'btn btn-link'}
                      onClick={handleChooseFileClick}
                    >
                      Choose file
                    </button>{' '}
                    to upload
                  </span>
                </div>
              </FileDrop>
            </>
          )}
          {attachmentData.fileToUpload && (
            <div>
              <i
                className={`fa ${
                  mimeTypeToIconMap[attachmentData.fileToUpload.type] ||
                  mimeTypeToIconMap['default']
                }`}
              />
              &nbsp;
              {attachmentData.fileToUpload.name}
              &nbsp;
              <button
                className={'btn btn-link'}
                onClick={handleRemoveAttachment}
              >
                Remove
              </button>
            </div>
          )}
          {errors.fileToUpload && (
            <span
              id='upload-attachment__fileToUpload--error'
              className='help-block text-right'
            >
              {errors.fileToUpload}
            </span>
          )}
        </FormGroup>

        {error && <p className='bg-danger text-danger form__error'>{error}</p>}
        <button
          type='submit'
          className={`btn btn-primary pull-right ${
            isLoading && 'btn--loading'
          }`}
          onClick={handleUploadButtonClick}
        >
          {isLoading && <Glyphicon glyph='refresh' className='spinning' />}
          <span>{isLoading ? 'Uploading...' : 'Upload'}</span>
        </button>
      </form>
    </div>
  );
};

export default UploadActivityAttachment;
